import type { IntlShape } from "@formatjs/intl";
import { Descriptions, Typography } from "antd";
import { formatTotalTime } from "../../../../common/utils/formatTotalTime";
import { getUserDisplayName } from "../../../../common/utils/userDisplayName";
import type { TicketRetrieve } from "../../../generated-axios-client";
import styles from "./ReportDetailsPreview.module.css";

const EMPTY_VALUE = "—";

type UserNames = Parameters<typeof getUserDisplayName>[0];

function peopleList(users: Array<UserNames | null | undefined>): string | null {
  const names = users
    .filter((user): user is UserNames => Boolean(user))
    .map(getUserDisplayName);
  return names.length > 0 ? names.join(", ") : null;
}

function reportTurbineLabel(ticket: TicketRetrieve): string | null {
  const turbine = ticket.turbine;
  if (!turbine) return null;
  const label = turbine.park_number
    ? `${turbine.serial_number ?? ""} (Park No. ${turbine.park_number})`.trim()
    : (turbine.serial_number ?? "");
  return label || null;
}

/** The Work Order Details table exactly as the PDF prints it. */
export function ReportDetailsPreview({
  ticket,
  intl,
}: {
  ticket: TicketRetrieve;
  intl: IntlShape;
}) {
  const formatDate = (value: string | null | undefined) =>
    value ? intl.formatMessage({ id: "short_date" }, { value: new Date(value) }) : null;
  const formatDateTime = (value: string | null | undefined) =>
    value
      ? `${formatDate(value)} ${intl.formatMessage({ id: "time" }, { value: new Date(value) })}`
      : null;
  // no recorded time prints as "—", like a work order without check-ins
  const formatDuration = (seconds: number | null | undefined) =>
    formatTotalTime(seconds || null);

  const rows: Array<[string, string | null | undefined]> = [
    [
      intl.formatMessage({ id: "wo_report_detail_work_order" }),
      `#${ticket.ticket_number}`,
    ],
    [intl.formatMessage({ id: "wo_report_detail_name" }), ticket.name],
    [intl.formatMessage({ id: "ticket_service" }), ticket.service?.name],
    [intl.formatMessage({ id: "ticket_client_case_number" }), ticket.client_case_nr],
    [intl.formatMessage({ id: "table_column_client" }), ticket.client?.legal_name],
    [
      intl.formatMessage({ id: "dashboard_projects_column_project" }),
      ticket.project?.name,
    ],
    [intl.formatMessage({ id: "ticket_location" }), ticket.location?.name],
    [intl.formatMessage({ id: "table_column_turbine" }), reportTurbineLabel(ticket)],
    [intl.formatMessage({ id: "ticket_start" }), formatDate(ticket.start)],
    [
      intl.formatMessage({ id: "ticket_actual_start" }),
      formatDateTime(ticket.actual_start),
    ],
    [
      intl.formatMessage({ id: "wo_report_detail_sent_to_review" }),
      formatDateTime(ticket.sent_to_review_at),
    ],
    ...(ticket.actual_end
      ? ([
          [
            intl.formatMessage({ id: "ticket_actual_end" }),
            formatDateTime(ticket.actual_end),
          ],
        ] as Array<[string, string | null]>)
      : []),
    [
      intl.formatMessage({ id: "ticket_total_time_label" }),
      formatDuration(ticket.total_time),
    ],
    [
      intl.formatMessage({ id: "ticket_total_standby_time_label" }),
      formatDuration(ticket.total_standby_time),
    ],
    [
      intl.formatMessage({ id: "wo_report_detail_technicians" }),
      peopleList(ticket.assignees.map((row) => row.assignee)),
    ],
    [
      intl.formatMessage({ id: "ticket_coordinator" }),
      peopleList(ticket.coordinators.map((row) => row.coordinator)),
    ],
    [
      intl.formatMessage({ id: "ticket_manager" }),
      peopleList(ticket.managers.map((row) => row.manager)),
    ],
    [intl.formatMessage({ id: "ticket_summary" }), ticket.summary_text],
  ];

  return (
    <Descriptions
      bordered
      size="small"
      column={1}
      items={rows.map(([label, value]) => ({
        key: label,
        label,
        children: value ? (
          <Typography.Text className={styles.multiline}>{value}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">{EMPTY_VALUE}</Typography.Text>
        ),
      }))}
    />
  );
}
